
import { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import type { PEOProvider } from '@/types/peo.types';

/**
 * Hook for handling bulk provider deletion
 */
export const useProviderBulkDelete = (
  providers: PEOProvider[],
  handleProviderDelete: (providerId: string) => Promise<boolean>
) => {
  const { toast } = useToast();
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [deleteProgress, setDeleteProgress] = useState<{ total: number; completed: number }>({ total: 0, completed: 0 });

  // Handle bulk deletion of providers
  const handleBulkDelete = async (): Promise<boolean> => {
    if (selectedProviders.length === 0) {
      console.log('No providers selected for deletion');
      return false;
    }
    
    try {
      console.log(`Bulk deleting ${selectedProviders.length} providers`);
      setIsBulkDeleting(true);
      setDeleteProgress({ total: selectedProviders.length, completed: 0 });
      
      // Delete providers one by one using the passed handler
      let successCount = 0;
      for (const id of selectedProviders) {
        const success = await handleProviderDelete(id);
        if (success) successCount++;
        
        setDeleteProgress(prev => ({ 
          ...prev, 
          completed: prev.completed + 1 
        }));
      }
      
      const allSucceeded = successCount === selectedProviders.length;
      
      if (!allSucceeded) {
        console.log(`${successCount} of ${selectedProviders.length} providers deleted successfully`);
        toast({
          title: 'Partial Success',
          description: `Deleted ${successCount} of ${selectedProviders.length} providers`,
          variant: 'destructive',
        });
        clearSelectedProviders();
        return false;
      }
      
      toast({
        title: 'Success',
        description: `Successfully deleted ${successCount} providers`,
      });
      
      clearSelectedProviders();
      return true;
    } catch (error) {
      console.error('Bulk provider deletion error:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete providers',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Select/deselect a provider
  const handleSelectProvider = (providerId: string, selected: boolean) => {
    if (selected) {
      setSelectedProviders(prev => [...prev, providerId]);
    } else {
      setSelectedProviders(prev => prev.filter(id => id !== providerId));
    }
  };

  // Select/deselect all providers
  const handleSelectAllProviders = (selected: boolean) => {
    if (selected) {
      const allIds = providers.map(provider => provider.id);
      setSelectedProviders(allIds);
    } else {
      clearSelectedProviders();
    }
  };

  // Clear selected providers
  const clearSelectedProviders = () => {
    setSelectedProviders([]);
  };

  return { 
    handleBulkDelete,
    isBulkDeleting,
    selectedProviders,
    handleSelectProvider,
    handleSelectAllProviders,
    clearSelectedProviders,
    deleteProgress
  };
};
